import React, { memo, useMemo, useRef } from 'react';
import { Whisper } from 'rsuite';
import { CurrencyInput, InputAddon, InputGroup } from '../../../../../../components';
import { IPAMaths } from '../../../../../../lib';
import { useEditedPriceManagerStore } from '../../../../../../store/priceManagerStore';
import { VariationBoxIndicator } from '../../../../Negociacoes/pages/NegociacaoFornecedor/components';
import { CompetitivenessLimitInfo } from '../../CompetitivenessLimitInfo';
import { BrokenLimitFilterValue, getBrokenLimitWarning } from '../../../utils/brokenLimitWarningUtils';
import { BrokenLimitWarningIcon } from '../../BrokenLimitWarningIcon';
import { isCpiOutsideLimit, resolveCpiLimitSkin } from '../../limitConformity';
import { BaseCell } from '../BaseCell';
import { BaseCellWithUpdateCampoProps } from '../BaseCell/BaseCell';

type CpiCellProps = BaseCellWithUpdateCampoProps & {
  brokenLimitFilter?: BrokenLimitFilterValue;
};

const { MainContainer, SubContainer } = BaseCell;

const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, className: string) => {
  if (e.key === 'Enter') {
    e.preventDefault();

    const currentInput = e.currentTarget;

    const nodeList = document.querySelectorAll(`.${className}`);
    const currentIndex = Array.from(nodeList).indexOf(currentInput);
    const nextIndex = currentIndex + 1;

    const nextInput = nextIndex < nodeList.length ? (nodeList[nextIndex] as HTMLInputElement) : null;

    currentInput.blur();

    if (nextInput) {
      setTimeout(() => {
        nextInput.focus();
        setTimeout(() => {
          nextInput.select();
        }, 100);
      }, 50);
    }
  }
};

const CpiCell = ({ rowData, index, dataKey, handleUpdateCampo, handleBlur, brokenLimitFilter = null, ...props }: CpiCellProps) => {
  const { novaCompetitividade, competitorsPrice, novaMargem, price, retailPrice, retailCpi } = rowData;
  const isPendingEditedPrice = useEditedPriceManagerStore(state => Boolean(state.pendingEditedPriceRows[rowData.productsToBePricedId]));
  const hasEditedSinceFocusRef = useRef(false);

  const cpiVariation = useMemo(() => {
    return IPAMaths.variation.cpi(novaCompetitividade, retailCpi);
  }, [novaCompetitividade, retailCpi]);

  const localBroken = isCpiOutsideLimit(rowData);
  const skin = useMemo(() => resolveCpiLimitSkin(rowData, brokenLimitFilter), [rowData, brokenLimitFilter]);

  const brokenLimitWarning = useMemo(
    () => getBrokenLimitWarning({ rowData, localBroken, type: 'COMPETITIVENESS', brokenLimitFilter }),
    [rowData, localBroken, brokenLimitFilter],
  );

  return (
    <BaseCell rowData={rowData} {...props}>
      <MainContainer>
        <InputGroup>
          <CurrencyInput
            name={dataKey}
            value={novaCompetitividade || 0}
            skin={skin}
            className="nova-competitividade"
            disabled={isPendingEditedPrice || !competitorsPrice}
            decimalSeparator=","
            thousandSeparator="."
            precision={1}
            allowEmpty
            allowNegative={false}
            onFocus={() => {
              hasEditedSinceFocusRef.current = false;
            }}
            onBlur={() => {
              const wasEdited = hasEditedSinceFocusRef.current;
              hasEditedSinceFocusRef.current = false;

              if (wasEdited) handleBlur(rowData, index);
            }}
            onChangeEvent={(_e, _, v) => {
              hasEditedSinceFocusRef.current = true;
              handleUpdateCampo(dataKey, v, rowData, index);
            }}
            onKeyDown={e => handleEnterKeyPress(e, 'nova-competitividade')}
            selectOnFocus
          />
          <Whisper
            trigger="click"
            placement="bottomEnd"
            speaker={
              // @ts-ignore
              <CompetitivenessLimitInfo content={rowData} margin={novaMargem} cpi={novaCompetitividade} />
            }
          >
            <InputAddon>%</InputAddon>
          </Whisper>
        </InputGroup>
      </MainContainer>
      <SubContainer>
        {!!price && !!competitorsPrice && !!retailPrice ? <VariationBoxIndicator className="box-indicator" value={cpiVariation} /> : <>--</>}
        <BrokenLimitWarningIcon warning={brokenLimitWarning} />
      </SubContainer>
    </BaseCell>
  );
};

export default memo(CpiCell);
